import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiRemote, setRemoteToken, getRemoteToken } from "@/lib/apiRemote";
import { api } from "@/lib/api";
import { adaptUser } from "@/lib/adapters";
import { toast } from "sonner";

const STAFF_ROLES = ["admin", "moderator"];
const isStaff = (u) => !!u && STAFF_ROLES.includes(u.role);
const mergeUsers = (remote, staff) => (staff ? { ...remote, role: staff.role, remote_role: remote.role } : remote);

async function provisionRemote(staff, email, password) {
  try {
    const { data } = await apiRemote.post("/auth/register", {
      email,
      password,
      name: staff.name || "Administrateur BODJE IMMO",
      phone: staff.phone || "",
      role: "agent",
      agency_name: "BODJE IMMO GUINÉE",
    });
    const token = data.token || data.access_token;
    if (!token) return null;
    setRemoteToken(token);
    return data.user || data;
  } catch {
    return null;
  }
}

export const LOCAL_ONLY_PUBLISH_MSG =
  "Votre session n'est pas reliée à l'application mobile BODJE IMMO : la publication est impossible. Déconnectez-vous puis reconnectez-vous avec votre e-mail et votre mot de passe pour relier automatiquement votre compte.";

export const LEGACY_MSG =
  "Ce compte n'existe que sur l'ancien site. Connectez-vous avec l'e-mail et le mot de passe de votre compte BODJE IMMO (application mobile), ou créez un compte.";
export const LOCAL_ONLY_MSG =
  "Votre session n'est pas reliée à l'application mobile BODJE IMMO : la messagerie est indisponible. Déconnectez-vous puis reconnectez-vous avec votre e-mail et votre mot de passe pour relier automatiquement votre compte.";

const AuthContext = createContext(null);

/**
 * Primary auth source of truth = remote BODJE IMMO backend.
 * JWT token (localStorage "bodje_token") is sent as Authorization: Bearer.
 *
 * Local site auth (cookie session) is still kept opportunistically so the
 * existing admin dashboard + site-only features (reports, VAPID push,
 * payment history, Stripe/mock Mobile Money) stay reachable for the admin.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getRemoteToken();
    const [remote, local] = await Promise.all([
      token ? apiRemote.get("/auth/me").then((r) => r.data.user || r.data).catch(() => null) : null,
      api.get("/auth/me", { validateStatus: () => true }).then((r) => (r.status === 200 ? r.data.user || r.data : null)).catch(() => null),
    ]);
    if (token && !remote) setRemoteToken(null);
    const staff = isStaff(local) ? local : null;
    if (remote) {
      setUser(mergeUsers(adaptUser(remote), staff));
    } else if (staff) {
      setUser(adaptUser({ ...staff, _is_local_only: true }));
    } else {
      if (local) {
        api.post("/auth/logout").catch(() => {});
        toast.info(LEGACY_MSG, { duration: 9000 });
      }
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const localReq = api
      .post("/auth/login", { email, password }, { validateStatus: () => true, timeout: 10000 })
      .then((r) => (r.status === 200 ? r.data.user || r.data : null))
      .catch(() => null);
    let remote = null;
    let remoteError = null;
    try {
      const { data } = await apiRemote.post("/auth/login", { email, password });
      const token = data.token || data.access_token;
      if (token) setRemoteToken(token);
      remote = data.user || data;
    } catch (err) {
      if (err?.response?.status !== 401) {
        throw new Error("Le serveur ne répond pas pour le moment. Vérifiez votre connexion et réessayez.");
      }
      remoteError = err;
    }
    const local = await localReq;
    const staff = isStaff(local) ? local : null;
    if (!remote && staff) remote = await provisionRemote(staff, email, password);
    let u;
    if (remote) {
      u = mergeUsers(adaptUser(remote), staff);
    } else if (staff) {
      u = adaptUser({ ...staff, _is_local_only: true });
      toast.warning(
        "Impossible de relier ce compte à l'application mobile : un compte avec cet e-mail y existe déjà avec un autre mot de passe. Utilisez le même mot de passe sur le site et dans l'application.",
        { duration: 10000 }
      );
    } else if (local) {
      api.post("/auth/logout").catch(() => {});
      throw new Error(LEGACY_MSG);
    } else {
      throw remoteError || new Error("Email ou mot de passe incorrect");
    }
    setUser(u);
    return u;
  };

  const register = async (payload) => {
    const body = {
      email: payload.email,
      password: payload.password,
      name: payload.name,
      phone: payload.phone || "",
      role: payload.role || "client",
    };
    const { data } = await apiRemote.post("/auth/register", body);
    const token = data.token || data.access_token;
    if (token) setRemoteToken(token);
    const u = adaptUser(data.user || data);
    setUser(u);
    try {
      await api.post("/auth/register", {
        email: payload.email,
        password: payload.password,
        name: payload.name,
        phone: payload.phone || "",
      }, {
        validateStatus: () => true,
      });
    } catch (_e) {}
    return u;
  };

  const logout = async () => {
    setRemoteToken(null);
    setUser(null);
    try {
      await api.post("/auth/logout");
    } catch {}
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
